/**
 * Configurações → Financeiro (Redesign Rodada 2 · LOTE 1).
 *
 * Lista as contas correntes do Omie (sync) com um toggle "exibir". As contas
 * exibidas governam TODO o financeiro (saldo + lançamentos dos indicadores).
 * Default: só Sicredi AG/Matheus ativas. Conta nova no Omie aparece aqui
 * automaticamente (via sync) pra ativar.
 */
import { useEffect, useState } from 'react';

import { api } from '@/lib/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Switch,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Alert,
} from '@/components/ui';

interface ContaConfig {
  empresaId: string;
  contaCodigo: string;
  nome: string | null;
  tipo: string | null;
  codigoBanco: string | null;
  agencia: string | null;
  numeroConta: string | null;
  inativo: string | null;
  saldo: number | null;
  exibir: number;
  /** CONTAS-SO-DESPESA (21/08): exibida SÓ pelo lado da despesa. */
  soDespesa: number;
}

const EMPRESA_NOME: Record<string, string> = {
  'empresa-igm-ag': 'AG DOS SANTOS',
  'empresa-igm-mussi': 'MATHEUS MUSSI',
};

export function FinanceiroContasConfigCard() {
  const [items, setItems] = useState<ContaConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  function load() {
    setLoading(true);
    setErr(null);
    // Promise.resolve + coerção de array: blindagem contra resposta com shape
    // inesperado (e contra mock não-Promise em testes) — nunca deixa `items`
    // virar undefined (auditoria pós-smoke).
    Promise.resolve(api.get<{ items: ContaConfig[] }>('/api/v1/admin/financeiro/contas-config'))
      .then((r) => setItems(Array.isArray(r?.items) ? r.items : []))
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  // CONTAS-SO-DESPESA (21/08): o POST sempre manda os DOIS campos — mandar só
  // um apagaria o outro no upsert. Um toggle de cada vez, otimista.
  async function toggle(c: ContaConfig, patch: { exibir?: boolean; soDespesa?: boolean }) {
    const key = `${c.empresaId}:${c.contaCodigo}`;
    const exibir = patch.exibir ?? c.exibir === 1;
    const soDespesa = patch.soDespesa ?? c.soDespesa === 1;
    setSavingKey(key);
    setItems((prev) =>
      prev.map((x) =>
        x.empresaId === c.empresaId && x.contaCodigo === c.contaCodigo
          ? { ...x, exibir: exibir ? 1 : 0, soDespesa: soDespesa ? 1 : 0 }
          : x,
      ),
    );
    try {
      await api.post('/api/v1/admin/financeiro/contas-config', {
        empresaId: c.empresaId,
        contaCodigo: c.contaCodigo,
        exibir,
        soDespesa,
      });
    } catch (e) {
      setErr((e as Error).message);
      load(); // reverte pro estado real
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Contas consideradas no Financeiro</CardTitle>
        <CardDescription>
          Os indicadores financeiros consideram <strong>só as contas ligadas aqui</strong> (saldo e
          lançamentos). Padrão: apenas Sicredi (AG + Matheus) ativas. Ligue outra conta pra incluí-la.
          Conta nova cadastrada no Omie aparece aqui automaticamente. <strong>Só despesas</strong>:
          a conta entra apenas pelo lado do que sai (as compras contam como despesa; o saldo e os
          recebimentos dela ficam fora) — é o caso dos cartões e do PagBank.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {err && (
          <Alert variant="danger" className="mb-3">
            {err}
          </Alert>
        )}
        {loading ? (
          <div className="text-xs text-ink-3 dark:text-ink-dark-3 italic py-4">Carregando contas…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-ink-3 dark:text-ink-dark-3 italic py-4">
            Nenhuma conta corrente sincronizada ainda (o sync do Omie roda a cada 15 min).
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Conta</TableHead>
                <TableHead>Banco / Agência / Nº</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Considerar</TableHead>
                <TableHead>Só despesas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => {
                const key = `${c.empresaId}:${c.contaCodigo}`;
                const sicredi = c.codigoBanco === '748';
                const ativa = c.inativo !== 'S';
                return (
                  <TableRow key={key} data-testid={`conta-config-${key}`}>
                    <TableCell className="text-ink-2 dark:text-ink-dark-2">
                      {EMPRESA_NOME[c.empresaId] ?? c.empresaId}
                    </TableCell>
                    <TableCell>
                      <span className="text-ink-1 dark:text-ink-dark-1">{c.nome ?? '—'}</span>{' '}
                      {sicredi && (
                        <Badge variant="success" className="ml-1">
                          Sicredi
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-ink-3 dark:text-ink-dark-3 text-xs">
                      {[c.codigoBanco, c.agencia, c.numeroConta].filter(Boolean).join(' · ') || '—'}
                    </TableCell>
                    <TableCell>
                      {ativa ? (
                        <Badge variant="neutral">Ativa</Badge>
                      ) : (
                        <Badge variant="warning">Inativa</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={c.exibir === 1}
                        disabled={savingKey === key}
                        onChange={(e) => toggle(c, { exibir: e.target.checked })}
                        aria-label={`Considerar ${c.nome ?? c.contaCodigo}`}
                      />
                    </TableCell>
                    <TableCell>
                      {/* Só faz sentido em conta considerada — fora dela, nada entra. */}
                      <Switch
                        checked={c.soDespesa === 1}
                        disabled={savingKey === key || c.exibir !== 1}
                        onChange={(e) => toggle(c, { soDespesa: e.target.checked })}
                        aria-label={`Só despesas de ${c.nome ?? c.contaCodigo}`}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
